/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import Preloader from './components/Preloader';
import './index.css';

// CRITICAL: Suppress PDF.js JPEG2000 warnings IMMEDIATELY (before imports)
// These warnings are expected during PDF merge when re-encoding JPEG2000 → standard JPEG
// The merge process handles failures gracefully with placeholders
(function suppressJPEG2000Warnings() {
  if (typeof window === 'undefined') return;
  
  // Create filter function for JPEG2000-related messages
  const isJPEG2000Warning = (args: any[]): boolean => {
    const message = args.join(' ').toString();
    return (
      message.includes('JpxImage') ||
      message.includes('JpxError') ||
      message.includes('OpenJPEG') ||
      message.includes('openjpeg') ||
      message.includes('Unable to decode image') ||
      message.includes('Dependent image') ||
      message.includes('wasmUrl') ||
      message.includes('nullopenjpeg') ||
      message.includes('Failed to resolve module specifier') ||
      message.includes('Ensure that the `wasmUrl` API parameter')
    );
  };
  
  // Store originals
  const originalWarn = console.warn.bind(console);
  const originalLog = console.log.bind(console);
  const originalError = console.error.bind(console);
  
  // Override console methods BEFORE any other code runs
  console.warn = function(...args: any[]) {
    if (isJPEG2000Warning(args)) return;
    originalWarn(...args);
  };
  
  console.log = function(...args: any[]) {
    if (isJPEG2000Warning(args)) return;
    originalLog(...args);
  };
  
  console.error = function(...args: any[]) {
    if (isJPEG2000Warning(args)) return;
    originalError(...args);
  };
  
  // Also intercept worker messages if they use a different console
  const originalConsole = (window as any).console;
  if (originalConsole && originalConsole !== console) {
    ['warn', 'log', 'error'].forEach(method => {
      const original = originalConsole[method].bind(originalConsole);
      originalConsole[method] = function(...args: any[]) {
        if (isJPEG2000Warning(args)) return;
        original(...args);
      };
    });
  }
})();



// Install dev-only debug helpers (available as window.__NC_DEBUG)
if ((import.meta as any).env.DEV) {
  // Dynamic import so production bundles don't include debug helpers
  import('./utils/debugConsole').then(mod => mod.installGlobal()).catch(() => {});
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    {/* Top-level fixed background scene must live outside the scaled wrapper so position:fixed behaves correctly */}
    <div className="mystic-scene" aria-hidden="true">
      <div className="depth-layer">
        <div className="base-layer" />
        <div className="mid-layer" />
        <div className="far-layer" />
      </div>

      <div className="fog-layer fog-1" />
      <div className="fog-layer fog-2" />
      <div className="fog-layer fog-3" />

      <div className="mist mist-1" />
      <div className="mist mist-2" />

      <div className="orbs">
        <div className="orb orb-a" />
        <div className="orb orb-b" />
        <div className="orb orb-c" />
      </div>

      {/* PERFORMANCE FIX: 600 particles removed - was causing severe lag */}
      {/* <div id="particle-constellation">
        {Array.from({ length: 600 }).map((_, i) => (
          <span key={i} className={`p-${i % 60}`} />
        ))}
      </div> */}

      <div className="sprites">
        <div className="sprite s1" />
        <div className="sprite s2" />
        <div className="sprite s3" />
      </div>

  {/* pulses removed per user request - large balloons removed */}

      {/* PERFORMANCE FIX: Heavy animated layers removed for smoother scrolling */}
      {/* <div className="wisp w1" />
      <div className="wisp w2" />

      <div className="energy-stream e1" />
      <div className="energy-stream e2" /> */}
    </div>

    {/* App wrapper + Preloader overlay:
        - React Preloader component shows while the app initializes
        - No inline preloader needed - React preloader handles all loading states
    */}
    <RootWithPreloader />
  </React.StrictMode>,
);

function RootWithPreloader(): JSX.Element {
  const [showPreloader, setShowPreloader] = React.useState(true);

  React.useEffect(() => {
    // Keep React Preloader visible for a minimum duration to ensure smooth loading experience
    const minVisible = 600; // ms - shorter since there's no inline preloader to replace
    const t = setTimeout(() => {
      setShowPreloader(false);
    }, minVisible);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {showPreloader && <Preloader />}
      <div style={{ visibility: showPreloader ? 'hidden' : 'visible' }}>
        <App />
      </div>
    </>
  );
}